import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, X, Briefcase } from "lucide-react";

const EMPTY = {
  title: "",
  section: "auto",
  contract_type: "",
  location: "Casnigo (BG)",
  description: "",
  requirements: [],
  published: true,
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [reqDraft, setReqDraft] = useState("");

  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); setReqDraft(""); };
  const startEdit = (j) => {
    setEditing(j.id);
    setForm({
      title: j.title,
      section: j.section || "auto",
      contract_type: j.contract_type || "",
      location: j.location || "",
      description: j.description || "",
      requirements: j.requirements || [],
      published: j.published,
    });
    setReqDraft("");
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setReqDraft(""); };

  const addRequirement = () => {
    const v = reqDraft.trim();
    if (!v) return;
    setForm((f) => ({ ...f, requirements: [...f.requirements, v] }));
    setReqDraft("");
  };
  const removeRequirement = (i) => setForm((f) => ({ ...f, requirements: f.requirements.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title?.trim() || !form.description?.trim()) {
      toast.error("Compila almeno titolo e descrizione.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/jobs", form);
        toast.success("Posizione creata");
      } else {
        await api.put(`/admin/jobs/${editing}`, form);
        toast.success("Posizione aggiornata");
      }
      await load();
      cancel();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa posizione?")) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Posizione eliminata");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <span className="label-eyebrow text-gray-500">Carriere</span>
          <h1 className="font-display uppercase text-4xl mt-1">Lavora con noi</h1>
        </div>
        <button onClick={startNew} className="btn-orange label-eyebrow px-5 py-3 inline-flex items-center gap-2" data-testid="job-new">
          <Plus size={14} /> Nuova posizione
        </button>
      </div>

      {editing && (
        <div className="card-flat p-6 md:p-8 mt-8 space-y-4" data-testid="job-editor">
          <div className="flex items-center justify-between">
            <h2 className="font-display uppercase text-2xl">{editing === "new" ? "Nuova posizione" : "Modifica"}</h2>
            <button onClick={cancel} className="text-gray-500"><X size={18} /></button>
          </div>

          <Field label="Titolo*" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="es. Meccanico auto" tid="job-title" />

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="label-eyebrow text-gray-500 block mb-2">Reparto*</label>
              <select
                value={form.section}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
                className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-[color:var(--nsa-orange)]"
                data-testid="job-section"
              >
                <option value="auto">Reparto Auto</option>
                <option value="moto">Reparto Moto</option>
                <option value="generale">Generale (entrambi)</option>
              </select>
            </div>
            <Field label="Tipo contratto" value={form.contract_type} onChange={(v) => setForm({ ...form, contract_type: v })} placeholder="es. Full-time · Tempo indeterminato" tid="job-contract" />
            <Field label="Sede" value={form.location} onChange={(v) => setForm({ ...form, location: v })} placeholder="es. Casnigo (BG)" tid="job-location" />
          </div>

          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">
              Descrizione* <span className="text-gray-400 normal-case tracking-normal">(supporta **grassetto** e paragrafi separati da riga vuota)</span>
            </label>
            <textarea
              rows={7}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3 font-mono text-sm"
              placeholder={"Descrivi la posizione, mansioni, cosa cerchi, cosa offri..."}
              data-testid="job-description"
            />
          </div>

          <div>
            <label className="label-eyebrow text-gray-500 block mb-2">
              Requisiti <span className="text-gray-400 normal-case tracking-normal">(uno per riga, opzionali)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={reqDraft}
                onChange={(e) => setReqDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRequirement(); } }}
                className="flex-1 border border-gray-300 px-3 py-2 focus:outline-none focus:border-gray-900"
                placeholder="es. Almeno 2 anni di esperienza"
                data-testid="job-req-input"
              />
              <button type="button" onClick={addRequirement} className="label-eyebrow px-4 py-2 bg-gray-900 text-white hover:bg-[color:var(--nsa-orange)] transition-colors" data-testid="job-req-add">
                Aggiungi
              </button>
            </div>
            {form.requirements.length > 0 && (
              <ul className="mt-3 space-y-2">
                {form.requirements.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 bg-gray-50 px-3 py-2 text-sm">
                    <span>{r}</span>
                    <button type="button" onClick={() => removeRequirement(i)} className="text-gray-500 hover:text-red-600" data-testid={`job-req-remove-${i}`}>
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <label className="flex items-center gap-2 label-eyebrow text-gray-600">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              data-testid="job-published"
            />
            Pubblicata (visibile sul sito)
          </label>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="btn-orange label-eyebrow px-6 py-3 disabled:opacity-60" data-testid="job-save">
              {saving ? "Salvataggio…" : "Salva"}
            </button>
            <button onClick={cancel} disabled={saving} className="border border-gray-300 label-eyebrow px-6 py-3 disabled:opacity-60">Annulla</button>
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-3">
        {jobs.length === 0 ? (
          <div className="card-flat p-10 text-center text-gray-500" data-testid="jobs-empty-admin">
            Nessuna posizione. Clicca "Nuova posizione" per crearne una.
          </div>
        ) : (
          jobs.map((j) => (
            <div key={j.id} className="card-flat p-5 flex items-center gap-4" data-testid={`job-row-${j.id}`}>
              <div className="w-11 h-11 flex items-center justify-center bg-[color:var(--nsa-orange)]/10 text-[color:var(--nsa-orange)] shrink-0">
                <Briefcase size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-display uppercase text-xl">{j.title}</h3>
                  <span className={`label-eyebrow text-[10px] px-2 py-0.5 ${j.published ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                    {j.published ? "PUBBLICATA" : "BOZZA"}
                  </span>
                </div>
                <div className="mt-1 text-sm text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span>{j.section === "auto" ? "Reparto Auto" : j.section === "moto" ? "Reparto Moto" : "Generale"}</span>
                  {j.contract_type && <span>· {j.contract_type}</span>}
                  {j.location && <span>· {j.location}</span>}
                </div>
              </div>
              <button onClick={() => startEdit(j)} className="p-2 border border-gray-300 hover:border-gray-900" data-testid={`job-edit-${j.id}`} title="Modifica">
                <Edit3 size={16} />
              </button>
              <button onClick={() => remove(j.id)} className="p-2 border border-gray-300 hover:border-red-500 hover:text-red-500" data-testid={`job-delete-${j.id}`} title="Elimina">
                <Trash2 size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, tid }) {
  return (
    <div>
      <label className="label-eyebrow text-gray-500 block mb-2">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-300 focus:border-gray-900 outline-none px-3 py-3"
        data-testid={tid}
      />
    </div>
  );
}
